import { useState } from "react";
import { Button } from "./ui/button";
import { Users, BedDouble, Bath, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK =
  "https://images.unsplash.com/photo-1554612292-c175942fb8c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function RoomCard({ room, emphasis = false, onBook }) {
  const photos = (room.photos && room.photos.length > 0) ? room.photos : [FALLBACK];
  const [idx, setIdx] = useState(0);

  const prev = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i - 1 + photos.length) % photos.length);
  };
  const next = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i + 1) % photos.length);
  };

  return (
    <article
      data-testid={`room-card-${room.slug}`}
      className={`group relative overflow-hidden rounded-2xl bg-card border border-border/60 hover:border-primary/40 transition-all duration-500 shadow-sm hover:shadow-xl flex flex-col ${
        emphasis ? "md:col-span-1" : ""
      }`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {photos.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt={`${room.name} photo ${i + 1}`}
            loading={i === 0 ? "eager" : "lazy"}
            data-testid={`room-${room.slug}-photo-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === idx ? "opacity-100" : "opacity-0"
            } group-hover:scale-105 transition-transform`}
            style={{ transitionProperty: "opacity, transform", transitionDuration: "700ms" }}
          />
        ))}

        <div className="absolute top-4 left-4 bg-background/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-sans font-semibold text-primary">
          ₹{room.price_per_night.toLocaleString("en-IN")} / night
        </div>

        {photos.length > 1 && (
          <>
            <button
              onClick={prev}
              data-testid={`room-${room.slug}-prev`}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={next}
              data-testid={`room-${room.slug}-next`}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
            >
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdx(i);
                  }}
                  aria-label={`Go to photo ${i + 1}`}
                  data-testid={`room-${room.slug}-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/85"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="p-7 md:p-9 flex flex-col gap-5 flex-1">
        <div>
          <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight text-foreground">
            {room.name}
          </h3>
          <p className="mt-2 font-sans text-sm text-muted-foreground">{room.short}</p>
        </div>

        <div className="flex flex-wrap gap-5 text-xs text-foreground/70 font-sans">
          <span className="flex items-center gap-2" data-testid={`room-${room.slug}-guests`}>
            <Users size={15} strokeWidth={1.5} /> {room.max_guests} guests
          </span>
          <span className="flex items-center gap-2">
            <BedDouble size={15} strokeWidth={1.5} /> {room.bedrooms} bedroom{room.bedrooms > 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-2">
            <Bath size={15} strokeWidth={1.5} /> {room.bathrooms} bath{room.bathrooms > 1 ? "s" : ""}
          </span>
        </div>

        <p className="font-sans text-sm text-foreground/80 leading-relaxed flex-1">
          {room.description}
        </p>

        {room.amenities?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {room.amenities.slice(0, 4).map((a) => (
              <span
                key={a}
                className="text-[11px] font-sans text-secondary/90 bg-secondary/5 px-3 py-1 rounded-full border border-secondary/15"
              >
                {a}
              </span>
            ))}
          </div>
        )}

        <Button
          data-testid={`check-availability-${room.slug}`}
          onClick={onBook}
          className="mt-2 self-start rounded-full bg-foreground text-background hover:bg-primary group/btn"
        >
          Check Availability
          <ArrowUpRight size={16} className="ml-1 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
        </Button>
      </div>
    </article>
  );
}
